import i18next from 'i18next';
import i18n from '../../i18n';

class DownloadHelper {

    static async createValidatedArrayBuffer(blob, extension) {
        if (!blob || !(blob instanceof Blob)) {
            throw new Error('Invalid blob for validation');
        }

        const maxSize = 100 * 1024 * 1024;
        if (blob.size > maxSize) {
            throw new Error('File size exceeds maximum allowed limit');
        }

        const extNormalized = (extension || '').replace('.', '').toLowerCase();
        const textLikeExts = ['txt', 'ris', 'json', 'xml'];

        if (textLikeExts.includes(extNormalized) || (blob.type && blob.type.startsWith('text/'))) {
            return new TextEncoder().encode(await blob.text()).buffer;
        }

        const arr = await blob.arrayBuffer();
        return arr;
    };

    static async saveFilePickerDownload(filename, extension, mimeType, blobContent) {
        if (typeof window.showSaveFilePicker !== 'function') {
            throw new Error('API showSaveFilePicker not supported.');
        }

        const options = {
            suggestedName: filename.replace(extension, ''), 
            types: [{
                description: 'Arquivo texto',
                accept: { [mimeType || 'text/plain']: [extension] },
            }],
            excludeAcceptAllOption: true,
        };

        const handle = await window.showSaveFilePicker(options);

        if (!handle || (handle.kind && handle.kind !== 'file')) {
            throw new Error(i18next.t('ErroAoLerArquivo'));
        }

        // Validate file name extension matches expected extension (if name available)
        const handleName = (handle.name || '').toString().toLowerCase();
        const hasValidExt = handleName ? handleName.endsWith(extension) : false;
        if (handleName && !hasValidExt) {
            throw new Error(i18next.t('ExtensaoSeleciondaInvalida'));
        }

        // Ensure we have write permission explicitly before creating writable
        let permissionStatus = 'prompt';
        if (typeof handle.queryPermission === 'function') {
            try {
                permissionStatus = await handle.queryPermission({ mode: 'readwrite' });
            } catch (qpErr) {
                // ignore and fall back to requestPermission
                permissionStatus = 'prompt';
            }
        }

        if (permissionStatus !== 'granted' && typeof handle.requestPermission === 'function') {
            try {
                permissionStatus = await handle.requestPermission({ mode: 'readwrite' });
            } catch (rpErr) {
                permissionStatus = 'denied';
            }
        }

        if (permissionStatus !== 'granted') {
            throw new Error(i18next.t('PermissaoDeLeituraArquivoNegada'));
        }

        if (typeof handle.createWritable !== 'function') {
            throw new Error(i18next.t('ErroAoLerArquivo'));
        }

        const writable = await handle.createWritable();

        // Enhanced blob validation before writing to file system
        if (!blobContent || !(blobContent instanceof Blob)) {
            throw new Error('Invalid data received for download');
        }

        // Additional security check: limit blob size to prevent abuse
        const maxSize = 100 * 1024 * 1024; // 100MB limit (increased from 50MB)
        if (blobContent.size > maxSize) {
            throw new Error('File size exceeds maximum allowed limit');
        }
        // Additional validation: ensure blob is not corrupted
        if (blobContent.size === 0) {
            throw new Error('Downloaded content is empty');
        }

        // For unknown extensions, only allow text/* blobs
        if (!(blobContent.type && blobContent.type.startsWith('text/'))) {
            throw new Error('Unsupported file type for download');
        }
        
        const bufferToWrite = await this.createValidatedArrayBuffer(blobContent, extension);

         try {
            // Ensure file is truncated before writing to avoid appending to existing content
            if (typeof writable.truncate === 'function') {
                try {
                    await writable.truncate(0);
                } catch (tErr) {
                    // ignore truncation errors and continue to write (best-effort)
                }
            }
            await writable.write(bufferToWrite);
        } finally {
            try {
                await writable.close();
            } catch (closeErr) {
                // swallow close errors
            }
        }
    };

    static async commonDownload(filename, blob) {
        if (!filename || typeof filename !== 'string') {
            filename = 'download';
        }
                
        filename = filename
            .trim()
            .substring(0, 255) 
            .replace(/[^a-zA-Z0-9._\-]/g, '_')
            .replace(/^\.+/, '')
            .replace(/\.+$/, ''); 
                
        if (!filename || filename.length === 0) {
            filename = 'download';
        }
        
        if (!blob || !(blob instanceof Blob)) {
            throw new Error(i18next.t('ErroAoGerarArquivo'));
        }
                
        // Additional security check: validate blob size
        const maxSize = 100 * 1024 * 1024; // 100MB limit
        if (blob.size > maxSize) {
            throw new Error(i18next.t('LimiteTamanhoArquivoExcedido'));
        }
                
        // Validate blob type - ensure it's safe for download
        const safeTypes = [
            'text/plain',
            'text/csv',
            'application/json',
            'application/xml',
            'application/marc',
            'application/x-research-info-systems'
        ];
                
        if (blob.type && !safeTypes.includes(blob.type)) {
            // Use a safer logging approach instead of console.warn
            ToastifyHelper.warning('Tipo de arquivo pode não ser seguro para download');
        }
        
        const link = document.createElement('a');
        link.style.display = 'none'; // Hide the link element
        
        try {
            const objectUrl = URL.createObjectURL(blob);
            if (!objectUrl || 
                !objectUrl.startsWith('blob:') || 
                objectUrl.includes('<') || 
                objectUrl.includes('>') ||
                objectUrl.includes('script:') ||
                objectUrl.includes('data:')) {
                if (objectUrl) {
                    URL.revokeObjectURL(objectUrl);
                }
                throw new Error(i18n.t('ErroAoGerarArquivo') + " - Invalid URL");
            }
            
            const sanitizedFilename = filename
                .replace(/[^\w\-_.]/g, '_')
                .substring(0, 100);
            
            if (!sanitizedFilename) {
                URL.revokeObjectURL(objectUrl);
                throw new Error(i18n.t('ErroAoGerarArquivo') + " - Invalid filename");
            }
            
            try {
                const parsed = new URL(objectUrl);
                if (parsed.protocol !== 'blob:') {
                    URL.revokeObjectURL(objectUrl);
                    throw new Error(i18n.t('ErroAoGerarArquivo') + " - Invalid URL Protocol");
                }
                link.setAttribute('href', parsed.toString());
            } catch (err) {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
                throw new Error(i18n.t('ErroAoGerarArquivo') + " - Invalid object URL");
            }

            link.download = sanitizedFilename;
            link.setAttribute('rel', 'noopener noreferrer');
            link.setAttribute('target', '_self');

            if (!document.body || 
                typeof document.body.appendChild !== 'function' ||
                typeof document.body.removeChild !== 'function') {
                URL.revokeObjectURL(objectUrl);
                throw new Error(i18n.t('ErroAoGerarArquivo') + " - DOM not safe");
            }

            // Temporarily append, click, and immediately remove
            const clickHandler = () => {
                // Remove immediately after click to minimize DOM exposure
                setTimeout(() => {
                    if (link.parentNode === document.body) {
                        document.body.removeChild(link);
                    }
                }, 10);
            };

            document.body.appendChild(link);
            link.addEventListener('click', clickHandler, { once: true });
            link.click();

            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            // Clean up in case of error
            if (link.href) {
                URL.revokeObjectURL(link.href);
            }
            if (link.parentNode) {
                link.parentNode.removeChild(link);
            }
            throw new Error(i18next.t('ErroAoGerarArquivo') + " - " + error.message);
        }
    }
}

export default DownloadHelper;